export type FlightPositionFt = { x: number; y: number; z: number };
/** A route vertex: floor-plane x/y, optionally pinning the flight's altitude at that vertex. */
export type FlightPlanarPointFt = readonly [number, number] | readonly [number, number, number];

/**
 * Lift an obstacle-aware floor-plane route into one continuous 3D flight. Altitude changes in
 * proportion to distance travelled along the routed XY polyline, so bends remain diagonal flight
 * instead of reintroducing the retired vertical-then-planar L.
 *
 * A vertex may pin its own altitude (third element). Between pinned altitudes — the origin and the
 * destination are always pinned — z still varies in proportion to planar distance. That is how a
 * flight HOLDS its height until it clears a ledge and only then descends (owner 2026-09-17, the
 * continuous world). Twin: ft3d_routed_flight_path_positions() in move_encounter_creature_logic.php.
 */
export function ft3dFlightPathFt(
  origin: FlightPositionFt,
  destination: FlightPositionFt,
  planarRoute: readonly FlightPlanarPointFt[] = [],
): FlightPositionFt[] {
  const start = { ...origin };
  const route = planarRoute
    .filter(point => Number.isFinite(point?.[0]) && Number.isFinite(point?.[1]))
    .map(point => ({
      x: Number(point[0]),
      y: Number(point[1]),
      z: point.length > 2 && Number.isFinite(point[2]) ? Number(point[2]) : null as number | null,
    }));
  if (route.length < 2) {
    return Math.hypot(
      destination.x - origin.x,
      destination.y - origin.y,
      destination.z - origin.z,
    ) <= 1e-9 ? [start] : [start, { ...destination }];
  }

  route[0] = { x: origin.x, y: origin.y, z: origin.z };
  route[route.length - 1] = { x: destination.x, y: destination.y, z: destination.z };
  const cumulative = [0];
  for (let index = 1; index < route.length; index += 1) {
    const previous = route[index - 1]!;
    const current = route[index]!;
    cumulative.push((cumulative[index - 1] ?? 0) + Math.hypot(current.x - previous.x, current.y - previous.y));
  }
  const planarLength = cumulative[cumulative.length - 1] ?? 0;
  const pinned = route.some((point, index) => index > 0 && index < route.length - 1 && point.z !== null);
  if (planarLength <= 1e-9 && !pinned) {
    return Math.abs(destination.z - origin.z) <= 1e-9 ? [start] : [start, { ...destination }];
  }
  return route.map((point, index) => {
    if (point.z !== null) return { x: point.x, y: point.y, z: point.z };
    let before = index - 1;
    while (before > 0 && route[before]!.z === null) before -= 1;
    let after = index + 1;
    while (after < route.length - 1 && route[after]!.z === null) after += 1;
    const span = (cumulative[after] ?? 0) - (cumulative[before] ?? 0);
    const t = span <= 1e-9 ? 0 : ((cumulative[index] ?? 0) - (cumulative[before] ?? 0)) / span;
    const zBefore = route[before]!.z ?? origin.z;
    const zAfter = route[after]!.z ?? destination.z;
    return { x: point.x, y: point.y, z: zBefore + (zAfter - zBefore) * t };
  });
}

export function ft3dFlightPathLengthFt(
  origin: FlightPositionFt,
  destination: FlightPositionFt,
  planarRoute: readonly FlightPlanarPointFt[] = [],
): number {
  const path = ft3dFlightPathFt(origin, destination, planarRoute);
  let total = 0;
  for (let index = 1; index < path.length; index += 1) {
    const previous = path[index - 1]!;
    const current = path[index]!;
    total += Math.hypot(
      current.x - previous.x,
      current.y - previous.y,
      current.z - previous.z,
    );
  }
  return total;
}

export type FlightRoofBox = {
  id?: string;
  x: { min: number; max: number };
  y: { min: number; max: number };
  floor: number;
  ceiling: number;
  navigationOpenGround?: boolean;
  creation?: { enclosure?: unknown; roofRemoved?: unknown; physicalCeilingFt?: unknown } | null;
};

/** boxIsRoofed() / ft_shape_box_is_roofed(): walls AND a ceiling that is still on. */
function flightBoxIsRoofed(box: FlightRoofBox): boolean {
  return String(box.creation?.enclosure ?? '').includes('ceiling') && box.creation?.roofRemoved !== true;
}

/**
 * Does one flight step pass through a closed room's ROOF (owner 2026-09-17: "block roofs")? Three ways through:
 * the body's top rises past the ceiling while the feet are below it; the feet themselves rise past it (a body already
 * taller than its room); the feet sink past it from above. Each is judged AT THE CROSSING POINT — inside the roofed
 * footprint, and not where the ceiling plane lies inside another room's volume (overlapping boxes are one open space,
 * the sight core's rule). Twin: ft3d_flight_step_crosses_roof() in move_encounter_creature_logic.php.
 */
export function ft3dFlightStepCrossesRoof(
  boxes: readonly FlightRoofBox[],
  from: FlightPositionFt,
  to: FlightPositionFt,
  bodyHeightFt: number,
): boolean {
  const eps = 1e-5;
  const crossingAt = (fromLevel: number, toLevel: number, ceiling: number) => {
    const span = toLevel - fromLevel;
    const t = Math.abs(span) <= 1e-12 ? 1 : Math.min(1, Math.max(0, (ceiling - fromLevel) / span));
    return { x: from.x + (to.x - from.x) * t, y: from.y + (to.y - from.y) * t };
  };
  for (const box of boxes) {
    if (box.navigationOpenGround || !flightBoxIsRoofed(box)) continue;
    const physical = box.creation?.physicalCeilingFt;
    const ceiling = typeof physical === 'number' && Number.isFinite(physical) ? physical : Number(box.ceiling);
    if (!Number.isFinite(ceiling)) continue;
    const crossings: Array<{ x: number; y: number }> = [];
    // The top's crossing is judged on its own: one step can carry both top and feet past the ceiling (Codex review).
    if (from.z + bodyHeightFt <= ceiling + eps && to.z + bodyHeightFt > ceiling + eps) {
      crossings.push(crossingAt(from.z + bodyHeightFt, to.z + bodyHeightFt, ceiling));
    }
    if (from.z + bodyHeightFt > ceiling + eps && to.z + bodyHeightFt <= ceiling + eps) {
      crossings.push(crossingAt(from.z + bodyHeightFt, to.z + bodyHeightFt, ceiling));
    }
    if (from.z < ceiling - eps && to.z >= ceiling - eps) crossings.push(crossingAt(from.z, to.z, ceiling));
    if (from.z >= ceiling - eps && to.z < ceiling - eps) crossings.push(crossingAt(from.z, to.z, ceiling));
    for (const at of crossings) {
      if (at.x <= box.x.min || at.x >= box.x.max || at.y <= box.y.min || at.y >= box.y.max) continue;
      const interior = boxes.some(other => other !== box && !other.navigationOpenGround
        && at.x > other.x.min + eps && at.x < other.x.max - eps && at.y > other.y.min + eps && at.y < other.y.max - eps
        && ceiling > other.floor + eps && ceiling < other.ceiling - eps);
      if (!interior) return true;
    }
  }
  return false;
}
